import { BasicPlaybook, User } from '@driftt/api-types'
import { Div } from '@driftt/tide-core'
import { Table } from '@driftt/tide-table'
import produce from 'immer'
import * as R from 'ramda'
import React, { useMemo } from 'react'
import { useQuery } from 'react-query'
import { useSelector } from 'react-redux'

import {
  getPrioritizedPlaybooks,
  getPrioritizedPlaybooksByWorkspace,
} from 'api/playbooks'
import { Fallback } from 'components/PlaybooksTable/components'
import {
  bucketPlaybooksByType,
  getColumns,
  makeRows,
} from 'components/PlaybooksTable/components/PrioritizeModal/helpers'
import useWorkspacesGeoTargeting from 'hooks/useWorkspacesGeoTargeting'

import styles from './styles.module.css'

export interface PriorityPayload {
  idPriorityList: number[]
  playbookTypes: {
    ABM: BasicPlaybook[]
    nonSpecial: BasicPlaybook[]
    outbound: BasicPlaybook[]
  }
}

interface Props {
  setHasPriorityChanged: (hasPriorityChanged: boolean) => void
  setPriorityPayload: (payload: PriorityPayload) => void
  priorityPayload: PriorityPayload
  selectedWorkspace?: { id: string }
  showWorkspaces?: boolean
  type: string
}

const PlaybookPriorityTable = ({
  setHasPriorityChanged,
  setPriorityPayload,
  priorityPayload,
  selectedWorkspace,
  showWorkspaces = false,
  type,
}: Props) => {
  const orgUsers: User[] = useSelector(R.path(['org', 'users'])) || []
  const { orgHasWorkspaces, allWorkspaces } = useWorkspacesGeoTargeting()
  const showWorkspaceColumn = showWorkspaces && orgHasWorkspaces

  const orgUsersById = useMemo(
    () => R.indexBy(R.prop('id'), orgUsers),
    [orgUsers]
  )

  const { isLoading } = useQuery({
    queryKey: ['prioritized-playbooks-load', type, selectedWorkspace?.id],
    queryFn: async () => {
      const { data } = !!selectedWorkspace
        ? await getPrioritizedPlaybooksByWorkspace(selectedWorkspace.id, {
            type,
          })
        : await getPrioritizedPlaybooks({ type })
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return bucketPlaybooksByType(data) as any
    },
    onSuccess: (data) => {
      setPriorityPayload(data)
    },
    onError: (error) => console.log(error),
    refetchOnWindowFocus: false,
  })

  const updatePriorityPayload = (payload: { idPriorityList: number[] }) => {
    const updatedPriorityPayload = produce(priorityPayload, (draft) =>
      R.merge(draft, payload)
    )

    setHasPriorityChanged(true)
    setPriorityPayload(updatedPriorityPayload)
  }

  const changePriority = (
    option: { label: string; value: number },
    playbook: BasicPlaybook
  ) => {
    const isId = (id: number) => id === playbook.id

    const idPriorityList = R.pathOr([], ['idPriorityList'], priorityPayload)
    const newIndex = option.value - 1

    const updatedPriorityList = R.insert(
      newIndex,
      playbook.id,
      R.reject(isId, idPriorityList)
    )

    updatePriorityPayload({ idPriorityList: updatedPriorityList })
  }

  const makeTableRows = () => {
    if (R.isEmpty(priorityPayload)) {
      return []
    }
    return makeRows({
      showWorkspaceColumn,
      allWorkspaces,
      changePriority,
      orgUsers: orgUsersById,
      priorityPayload,
    })
  }

  return (
    <Div className={styles.playbookPriorityTable}>
      <Table columns={getColumns(showWorkspaceColumn)} data={makeTableRows()} />
      <Fallback
        isPriority
        isEmpty={R.isEmpty(priorityPayload)}
        isLoading={isLoading}
      />
    </Div>
  )
}

export default PlaybookPriorityTable
